import { createApi } from '@reduxjs/toolkit/query/react';

import { AUTH_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const TagType = {
    SUPPLIER_USER_NAME_MAP: 'SUPPLIER_USER_NAME_MAP',
    ORGANIZATION_USERS: 'ORGANIZATION_USERS',
    ROLES: 'ROLES',
} as const;

export interface OrganizationUser {
    upn: string;
    full_name: string;
    role_name: string;
    display_name: string;
    sms_number: string | null;
    analytics_access: boolean;
}

export interface Role {
    role_name: string;
    display_name: string;
    description: string;
    permissions: string[];
}

export interface NewOrganizationUser {
    upn: string;
    given_name: string;
    family_name: string;
    name: string;
    password: string;
    role_name: string;
    sms_number: string | null;
    analytics_access: boolean;
}

export interface OrganizationUserChange {
    full_name: string;
    sms_number: string | null;
    role_name: string;
    analytics_access: boolean;
}

export const authApi = createApi({
    baseQuery: getBaseQuery(AUTH_API_URL),
    reducerPath: 'authApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getSupplierUserNameMap: build.query<Record<string, string>, void>({
            query: () => `profile/supplier/name_map`,
            providesTags: [TagType.SUPPLIER_USER_NAME_MAP],
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving user names.'),
        }),
        getOrganizationUsers: build.query<OrganizationUser[], void>({
            query: () => `supplier_users/organization_users/`,
            providesTags: [TagType.ORGANIZATION_USERS],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving user accounts.',
            ),
        }),
        getOrganizationUser: build.query<OrganizationUser, string>({
            query: (upn) => `supplier_users/organization_users/${encodeURIComponent(upn)}`,
            providesTags: (_result, _error, upn) => [{ type: TagType.ORGANIZATION_USERS, id: upn }],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving the user account.',
            ),
        }),
        getOrganizationUserRoles: build.query<Role[], void>({
            query: () => `supplier_users/organization_users/roles`,
            providesTags: [TagType.ROLES],
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving roles.'),
        }),
        createOrganizationUser: build.mutation<
            { authzero_user_created?: boolean },
            NewOrganizationUser
        >({
            query: (body) => ({
                url: `supplier_users/organization_users/`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [TagType.ORGANIZATION_USERS, TagType.SUPPLIER_USER_NAME_MAP],
            // Errors handled in UserAccountFormPage so 409 can map to an inline field error
            // without also firing a toast via handleRTKQueryError.
        }),
        updateOrganizationUser: build.mutation<void, { upn: string; body: OrganizationUserChange }>(
            {
                query: ({ upn, body }) => ({
                    url: `supplier_users/organization_users/${encodeURIComponent(upn)}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: [TagType.ORGANIZATION_USERS, TagType.SUPPLIER_USER_NAME_MAP],
                // Errors handled in UserAccountFormPage.
            },
        ),
        deleteOrganizationUser: build.mutation<void, { upn: string; reason: string }>({
            query: ({ upn, reason }) => ({
                url: `supplier_users/organization_users/${encodeURIComponent(upn)}`,
                method: 'DELETE',
                params: { reason },
            }),
            invalidatesTags: [TagType.ORGANIZATION_USERS, TagType.SUPPLIER_USER_NAME_MAP],
            // Errors handled in DeleteUserAccountDialog.
        }),
    }),
});

export const {
    useGetSupplierUserNameMapQuery,
    useGetOrganizationUsersQuery,
    useGetOrganizationUserQuery,
    useGetOrganizationUserRolesQuery,
    useCreateOrganizationUserMutation,
    useUpdateOrganizationUserMutation,
    useDeleteOrganizationUserMutation,
} = authApi;
